/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Escapes CSS selectors to handle special characters like colons in class names
 */
const escapeSelector = (selector: string): string => {
  try {
    if (selector.includes(':')) {
      return selector.replace(/([^\\]):(?![a-z-]+\()/g, '$1\\:');
    }
    return selector;
  } catch {
    console.error('Invalid CSS selector:', selector);
    return selector;
  }
};

/**
 * Validates if a selector is a valid CSS selector
 */
const isValidCSSSelector = (selector: string): boolean => {
  try {
    document.querySelector(selector);
    return true;
  } catch {
    return false;
  }
};

/**
 * Normalizes a selector by escaping special characters
 */
const normalizeSelector = (selector: string): string => {
  // Auto-escape colons in class names (Tailwind CSS support)
  let normalized = escapeSelector(selector);

  // Validate the normalized selector
  if (!isValidCSSSelector(normalized)) {
    // If still invalid, try with more aggressive escaping
    normalized = selector
      .replace(/:/g, '\\:')
      .replace(/\//g, '\\/')
      .replace(/\(/g, '\\(')
      .replace(/\)/g, '\\)')
      .replace(/\[/g, '\\[')
      .replace(/\]/g, '\\]');
  }

  return normalized;
};

/**
 * jQuery fallback handler for complex selectors
 */
export class jQueryFallback {
  private static jqueryLoaded = false;
  private static jqueryPromise: Promise<any> | null = null;
  private static jqueryCache: any = null;

  /**
   * Detects if a selector contains jQuery-specific syntax
   */
  static hasJQuerySelector(selector: string): boolean {
    // Only use jQuery for truly complex selectors that CSS can't handle
    const jqueryPatterns = [
      /:eq\(/,
      /:gt\(/,
      /:lt\(/,
      /:contains\(/,
      /:odd\b/,
      /:even\b/,
      /:header/,
      /:input/,
      /:text\b/,
      /:checkbox/,
      /:radio/,
      /:submit/,
      /:button/,
      /:visible/,
      /:hidden/,
      /:parent/,
    ];

    return jqueryPatterns.some((pattern) => pattern.test(selector));
  }

  /**
   * Detects existing jQuery on the page
   */
  static detectExistingJQuery(): any | null {
    const win = window as any;

    // Check cache first
    if (this.jqueryCache) return this.jqueryCache;

    // Check common jQuery variations
    if (win.jQuery && typeof win.jQuery === 'function') {
      return (this.jqueryCache = win.jQuery);
    }

    if (win.$ && typeof win.$ === 'function' && win.$.fn && win.$.fn.jquery) {
      return (this.jqueryCache = win.$);
    }

    // Check for jQuery in noConflict mode
    if (win.jQuery && !win.$ && typeof win.jQuery === 'function') {
      return (this.jqueryCache = win.jQuery);
    }

    return null;
  }

  /**
   * Loads jQuery dynamically if not already loaded
   */
  static async loadjQuery(): Promise<any> {
    // Check cache first
    if (this.jqueryCache) return this.jqueryCache;

    // Check for existing jQuery first
    const existingJQuery = this.detectExistingJQuery();
    if (existingJQuery) {
      this.jqueryLoaded = true;
      return existingJQuery;
    }

    // If we're already loading jQuery, wait for that
    if (this.jqueryPromise) {
      return this.jqueryPromise;
    }

    // Check if already loading to prevent multiple script tags
    if (document.querySelector('script[data-jquery-fallback="true"]')) {
      throw new Error('jQuery is already being loaded');
    }

    this.jqueryPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src =
        'https://cdnjs.cloudflare.com/ajax/libs/jquery/3.7.1/jquery.min.js';
      script.integrity =
        'sha512-v2CJ7UaYy4JwqLDIrZUI/4hqeoQieOmAZNXBeQyjo21dadnwR+8ZaIJVT8EE2iyI61OV8e6M8PP2/4hpQINQ/g==';
      script.crossOrigin = 'anonymous';
      script.setAttribute('data-jquery-fallback', 'true');

      script.onload = () => {
        const newJQuery = this.detectExistingJQuery();
        if (newJQuery) {
          this.jqueryLoaded = true;
          resolve(newJQuery);
        } else {
          reject(new Error('jQuery loaded but not detected'));
        }
      };

      script.onerror = () => {
        reject(new Error('Failed to load jQuery from CDN'));
      };

      document.head.appendChild(script);
    });

    return this.jqueryPromise;
  }

  /**
   * Query using jQuery if available, fallback to standard querySelector
   */
  static async queryWithjQuery(selector: string): Promise<Element | null> {
    try {
      const $ = await this.loadjQuery();
      const $element = $(selector);

      // jQuery returns a jQuery object, get the first DOM element
      return $element.length > 0 ? $element[0] : null;
    } catch (_error) {
      console.warn('Failed to load jQuery or query with jQuery:', _error);
      // Fallback to standard querySelector (will likely fail for jQuery selectors)
      try {
        return document.querySelector(selector);
      } catch (_e) {
        console.warn('Invalid CSS selector:', selector);
        return null;
      }
    }
  }
}

/**
 * Check if element is usable for interaction (supports both HTML and SVG elements)
 */
const isElementUsable = (element: Element): boolean => {
  if (!isElementVisible(element)) return false;

  const tagName = element.tagName.toLowerCase();

  // Don't accept body element unless it's the only option
  if (tagName === 'body') {
    return false;
  }

  // Handle SVG elements differently
  if (element instanceof SVGElement) {
    return isUsableSVGElement(element);
  }

  // For HTML elements, check dimensions
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return false;

  return true;
};

/**
 * Specialized check for SVG elements
 */
const isUsableSVGElement = (element: SVGElement): boolean => {
  const tagName = element.tagName.toLowerCase();

  // SVG elements that are always considered usable if visible
  const alwaysUsableSVGElements = new Set([
    'path',
    'circle',
    'rect',
    'ellipse',
    'line',
    'polyline',
    'polygon',
    'text',
    'tspan',
    'use',
    'image',
    'g', // groups can be interactive
  ]);

  if (alwaysUsableSVGElements.has(tagName)) {
    return true;
  }

  // For other SVG elements, check if they have meaningful content
  const rect = element.getBoundingClientRect();

  // SVG root elements should have dimensions
  if (tagName === 'svg') {
    return rect.width > 0 && rect.height > 0;
  }

  // For other SVG elements, check if they have attributes that suggest interactivity
  const hasInteractiveAttributes =
    element.hasAttribute('onclick') ||
    element.hasAttribute('onmousedown') ||
    element.hasAttribute('onmouseup') ||
    element.hasAttribute('onmouseover') ||
    element.hasAttribute('onmouseout') ||
    element.hasAttribute('href') ||
    element.hasAttribute('xlink:href') ||
    element.classList.contains('clickable') ||
    element.classList.contains('interactive');

  if (hasInteractiveAttributes) {
    return true;
  }

  // For remaining SVG elements, fall back to dimension check
  return rect.width > 0 && rect.height > 0;
};

/**
 * Enhanced visibility check that works with both HTML and SVG elements
 */
const isElementVisible = (element: Element): boolean => {
  if (!element) return false;

  // Quick elimination checks first
  if (element instanceof HTMLElement) {
    if (!element.offsetParent && element.tagName !== 'BODY') return false;
  }

  const rect = element.getBoundingClientRect();

  // For SVG elements, zero dimensions might still be valid (e.g., paths)
  if (!(element instanceof SVGElement)) {
    if (rect.width === 0 && rect.height === 0) return false;
  }

  // Check computed style
  const style = getComputedStyle(element);
  if (
    style.display === 'none' ||
    style.visibility === 'hidden' ||
    parseFloat(style.opacity) === 0
  ) {
    return false;
  }

  // SVG-specific visibility checks
  if (element instanceof SVGElement) {
    // Check SVG-specific style properties
    if (style.fill === 'none' && style.stroke === 'none') {
      // Element might still be usable if it has interactive attributes
      const hasInteractiveAttributes =
        element.hasAttribute('onclick') ||
        element.hasAttribute('href') ||
        element.hasAttribute('xlink:href');

      if (!hasInteractiveAttributes) return false;
    }

    // Check if parent SVG is visible
    const parentSVG = element.closest('svg');
    if (parentSVG && parentSVG !== element) {
      const parentRect = parentSVG.getBoundingClientRect();
      if (parentRect.width === 0 || parentRect.height === 0) return false;
    }
  }

  // Check if element is positioned at origin (common for hidden elements)
  // Skip this check for SVG elements as they often have valid zero positions
  if (!(element instanceof SVGElement)) {
    if (
      rect.top === 0 &&
      rect.left === 0 &&
      rect.right === 0 &&
      rect.bottom === 0 &&
      element.tagName !== 'BODY'
    ) {
      return false;
    }
  }

  return true;
};

/**
 * Enhanced getNearestNode function with jQuery fallback and CSS escaping
 */
export const getNearestNode = async (
  path: string,
  options?: {
    maxRetries?: number;
    retryDelay?: number;
    fast?: boolean;
    autoEscape?: boolean;
  }
): Promise<HTMLElement | null> => {
  if (typeof path !== 'string') {
    console.warn('Invalid path:', path);
    return null;
  }

  const {
    maxRetries = 3,
    retryDelay = 30,
    fast = true,
    autoEscape = true,
  } = options || {};

  let node: HTMLElement | null = null;
  let attempts = 0;

  // Normalize selector if auto-escape is enabled
  const selector = autoEscape ? normalizeSelector(path) : path;
  // Fast path: skip retries and parent fallback
  if (fast) {
    if (jQueryFallback.hasJQuerySelector(selector)) {
      node = (await jQueryFallback.queryWithjQuery(
        selector
      )) as HTMLElement | null;
    } else {
      try {
        node = document.querySelector(selector);
      } catch (_error) {
        console.warn('Invalid CSS selector:', selector);
        return null;
      }
    }
    // Validate that the element is actually visible and usable
    if (node && isElementUsable(node)) {
      return node;
    }
    return null;
  }

  const findElement = async (): Promise<HTMLElement | null> => {
    // Check if this selector needs jQuery
    if (jQueryFallback.hasJQuerySelector(selector)) {
      node = (await jQueryFallback.queryWithjQuery(
        selector
      )) as HTMLElement | null;
    } else {
      // Use standard querySelector for normal CSS selectors
      try {
        node = document.querySelector(selector);
      } catch (_error) {
        console.warn('Invalid CSS selector:', selector);
        return null;
      }
    }

    // Validate that the element is actually visible and usable
    if (node && isElementUsable(node)) {
      return node;
    }

    return null;
  };

  // Retry logic with exponential backoff
  while (attempts <= maxRetries) {
    node = await findElement();

    if (node) {
      return node;
    }

    attempts++;

    if (attempts <= maxRetries) {
      // Wait before retrying, with exponential backoff
      const delay = retryDelay * Math.pow(1.5, attempts - 1);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  // If we still haven't found the element, try parent path
  const parts = selector.split(' > ').slice(0, -1);
  if (parts.length > 0) {
    return getNearestNode(parts.join(' > '), options);
  }

  return null;
};

/**
 * Synchronous version that returns a Promise for jQuery selectors
 */
export const getNearestNodeSync = (
  path: string,
  options?: { autoEscape?: boolean }
): Element | Promise<Element | null> | null => {
  const { autoEscape = true } = options || {};

  // Normalize selector if auto-escape is enabled
  const selector = autoEscape ? normalizeSelector(path) : path;

  // Check if this selector needs jQuery
  if (jQueryFallback.hasJQuerySelector(selector)) {
    return getNearestNode(selector); // Return the async version
  }

  // Use standard querySelector for normal CSS selectors
  try {
    const node = document.querySelector(selector) as HTMLElement | null;
    if (node && isElementUsable(node)) {
      return node;
    }
  } catch (_error) {
    console.warn('Invalid CSS selector:', selector);
    // Silent fail for invalid CSS selectors
  }

  // If that fails, try parent path
  const parts = selector.split(' > ').slice(0, -1);
  if (parts.length === 0) {
    return null;
  }

  return getNearestNodeSync(parts.join(' > '), options);
};

// Track ongoing scroll operations to prevent race conditions
let currentScrollOperation: Promise<void> | null = null;

/**
 * Enhanced scrollToTaskElement function with retry logic, visibility checks, and race condition prevention
 */
export const scrollToTaskElement = async (
  taskElementPath?: string
): Promise<void> => {
  if (!taskElementPath) {
    console.warn('scrollToTaskElement: No taskElementPath provided');
    return;
  }

  // Ensure DOM is ready before attempting to scroll
  if (document.readyState === 'loading') {
    await new Promise((resolve) => {
      const handler = () => {
        document.removeEventListener('DOMContentLoaded', handler);
        resolve(void 0);
      };
      document.addEventListener('DOMContentLoaded', handler);
    });
  }

  // Check if we're in a post-modal state and add stabilization delay
  await ensureViewportStability();

  // Wait for any ongoing scroll operation to complete
  if (currentScrollOperation) {
    await currentScrollOperation;
  }

  // Create a new scroll operation
  currentScrollOperation = (async () => {
    try {
      let targetElement: HTMLElement | null = null;

      // Use optimized getNearestNode with auto-escaping
      targetElement = await getNearestNode(taskElementPath, {
        maxRetries: 5,
        retryDelay: 100,
        autoEscape: true,
      });

      if (!targetElement) {
        console.warn(
          `scrollToTaskElement: Element not found after retries: ${taskElementPath}`
        );
        throw new Error(`Element not found: ${taskElementPath}`);
      }

      if (!isElementVisible(targetElement)) {
        // Element exists but isn't visible, try to make it visible
        await expandCollapsedContainers(targetElement);

        // Give DOM time to update after expanding
        await new Promise((resolve) => setTimeout(resolve, 100));

        if (!isElementVisible(targetElement)) {
          console.warn(
            `scrollToTaskElement: Element found but not visible: ${taskElementPath}`
          );
          throw new Error(`Element not visible: ${taskElementPath}`);
        }
      }

      // Scroll to the element
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (error) {
      console.error(
        'scrollToTaskElement error:',
        error,
        'Path:',
        taskElementPath
      );
    }
  })();

  await currentScrollOperation;
  currentScrollOperation = null;
};

/**
 * Attempt to expand collapsed containers that might be hiding the element
 */
const expandCollapsedContainers = async (
  element: HTMLElement
): Promise<void> => {
  let parent = element.parentElement;

  while (parent) {
    // Look for common collapse/expand patterns
    const collapsedSelectors = [
      '[aria-expanded="false"]',
      '.collapsed',
      '.collapse:not(.show)',
      '.accordion-collapse:not(.show)',
      'details:not([open])',
      '.dropdown-menu:not(.show)',
      '.tab-pane:not(.active)',
      '.modal:not(.show)',
      '.offcanvas:not(.show)',
    ];

    for (const selector of collapsedSelectors) {
      const collapsedElement = parent.closest(selector);
      if (collapsedElement) {
        try {
          // Try to expand the element
          if (collapsedElement.tagName === 'DETAILS') {
            (collapsedElement as HTMLDetailsElement).open = true;
          } else if (collapsedElement.hasAttribute('aria-expanded')) {
            collapsedElement.setAttribute('aria-expanded', 'true');
            collapsedElement.classList.remove('collapsed');
          } else {
            // Try clicking on expand button
            const expandButton =
              collapsedElement.querySelector('[aria-expanded="false"]') ||
              collapsedElement.querySelector('.btn-toggle') ||
              collapsedElement.querySelector('.accordion-button');
            if (expandButton) {
              (expandButton as HTMLElement).click();
            }
          }

          // Give time for expansion animation
          await new Promise((resolve) => setTimeout(resolve, 50));
        } catch (error) {
          console.warn('Failed to expand collapsed container:', error);
        }
      }
    }

    parent = parent.parentElement;
  }
};

/**
 * Ensure viewport is stable after modal state changes
 */
const ensureViewportStability = async (): Promise<void> => {
  // Check for indicators that we might be in a post-modal state
  const modalElements = document.querySelectorAll(
    '[role="dialog"], .modal, [data-modal]'
  );
  const hasOpenModal = Array.from(modalElements).some((modal) => {
    const style = window.getComputedStyle(modal);
    return style.display !== 'none' && style.visibility !== 'hidden';
  });

  // If there are modal elements that recently closed, wait for animations to complete
  if (modalElements.length > 0 && !hasOpenModal) {
    // Wait for potential modal close animations to complete
    await new Promise((resolve) => setTimeout(resolve, 150));
  }

  // Ensure focus is restored to document body if needed
  if (document.activeElement && document.activeElement !== document.body) {
    // If focus is on a modal element that's no longer visible, restore focus
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement.closest('[role="dialog"], .modal, [data-modal]')) {
      document.body.focus();
    }
  }

  // Wait for any CSS transitions/animations to complete
  await new Promise((resolve) => setTimeout(resolve, 100));

  // Additional check for viewport stability
  let lastScrollY = window.scrollY;
  let stableCount = 0;

  // Wait for scroll position to stabilize
  while (stableCount < 3) {
    await new Promise((resolve) => setTimeout(resolve, 50));
    const currentScrollY = window.scrollY;

    if (Math.abs(currentScrollY - lastScrollY) < 1) {
      stableCount++;
    } else {
      stableCount = 0;
      lastScrollY = currentScrollY;
    }
  }
};
